import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { mapToObject } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { isUndefined, partition } from 'lodash';
import { getShapesForEventTracking } from '../../../cross-team-shared/annotation/shared/state/layers/shape-layer-state';
import { ObjectState } from '../../../cross-team-shared/annotation/shared/state/object-state';
import { ShapeState } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { MessagingService } from '../../../cross-team-shared/messaging/messaging.service';
import { BrowserControlService } from '../../../cross-team-shared/misc-services/browser-control.service';
import { MessageModalComponent } from '../../../cross-team-shared/misc-services/message-modal/message-modal.component';
import { isOrthographicVideoOutput } from '../../../cross-team-shared/misc-utils/output-utils';
import { AbstractAnnotationStore } from '../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { EventTrackingValue } from '../../../team-apis/core/event-tracking';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { FusionManager } from '../../outputs/annotation/annotation-shared/fusion/fusion-manager';
import { flipShapeYCoordinates } from '../../outputs/annotation/annotation-shared/fusion/projection/orthographic-utils';
import { OutputKeyboardShortcutService } from '../../outputs/annotation/annotation-shared/utils/output-keyboard-shortcut.service';
import { PushAndTrackService } from '../../outputs/annotation/annotation-shared/utils/push-and-track.service';
import { SelectionService } from '../../outputs/annotation/annotation-shared/utils/selection.service';
import { AnnotationCreateDeleteService } from '../misc-services/annotation-create-delete.service';
import { FeatureFlagService } from '../misc-services/feature-flag.service';
import { objectSequencesFeaturesErrorCause } from '../misc-services/object.service';
import { ShapeService } from '../misc-services/shape.service';
import { ClipboardAnnotationData } from './copy-paste-types';
import {
  PasteValidationError,
  PasteValidationService,
} from './paste-validation.service';

@Injectable()
export class AnnotationPastingService extends InjectableSuperclass {
  private store = inject(AbstractAnnotationStore);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private keyboardShortcutService = inject(OutputKeyboardShortcutService);
  private selectionService = inject(SelectionService);
  private browserControlService = inject(BrowserControlService);
  private matDialog = inject(MatDialog);
  private pasteValidationService = inject(PasteValidationService);
  private messagingService = inject(MessagingService);
  private pushAndTrackService = inject(PushAndTrackService);
  private annotationCreateDeleteService = inject(AnnotationCreateDeleteService);
  private fusionManager = inject(FusionManager);
  private featureFlagService = inject(FeatureFlagService);
  private shapeService = inject(ShapeService);

  startListening() {
    if (
      !this.taskWorkspaceStore.state().readOnly &&
      !this.taskWorkspaceStore.isUsingGenAi()
    ) {
      this.subscribeToPasteShapes();
    }
  }

  async pasteAnnotations(
    eventTrackingValue: EventTrackingValue,
    keyboardEvent?: KeyboardEvent,
  ) {
    const clipboardText = await this.browserControlService.readClipboardText();
    const copiedAnnotationData =
      this.validateAndParseDataForPasting(clipboardText);

    if (isUndefined(copiedAnnotationData)) {
      return;
    }

    keyboardEvent?.preventDefault(); // prevent browser paste

    try {
      const { shapes, objects } =
        this.createObjectsAndShapes(copiedAnnotationData);
      this.selectionService.setSelection(
        objects.map((object) => object.index),
        shapes.map((shape) => shape.index),
      );
      this.trackPasteEvent(eventTrackingValue, shapes, objects);
    } catch (error) {
      if ((error as Error).cause === objectSequencesFeaturesErrorCause) {
        this.messagingService.error((error as Error).message);
      }
    }
  }

  private createObjectsAndShapes({
    meta,
    shapes,
    objects,
  }: ClipboardAnnotationData) {
    if (meta.isForSingleFrame) {
      const { frameNumber } = this.store.state();
      shapes.forEach(
        (shape) => (shape.key_locations[0].frame_number = frameNumber),
      );
    }

    const shouldOffsetShapes = this.featureFlagService.hasFlag('copy-paste-v2');
    return this.annotationCreateDeleteService.createAnnotations(
      objects,
      shouldOffsetShapes ? this.offsetShapes(shapes) : shapes,
    );
  }

  private offsetShapes(shapes: ShapeState[]) {
    const { selectedOutputName, outputInfoByName } =
      this.taskWorkspaceStore.state();
    const contentConnector =
      this.fusionManager.getContentConnector(selectedOutputName);

    // eslint-disable-next-line prefer-const -- we need to edit shapesToOffset
    let [shapesToOffset, otherShapes] = partition(
      shapes,
      (shape) => shape.outputName === selectedOutputName,
    );
    const selectedOutput = outputInfoByName[selectedOutputName].output;
    const isOrthographic = isOrthographicVideoOutput(selectedOutput);

    if (isOrthographic) {
      shapesToOffset = shapesToOffset.map(flipShapeYCoordinates);
    }

    let offsetShapes =
      contentConnector.offsetShapesToMousePosition(shapesToOffset);
    if (isOrthographic) {
      offsetShapes = offsetShapes.map((shape) =>
        this.shapeService.groundShape(shape, selectedOutput.id),
      );
    }

    return [...offsetShapes, ...otherShapes];
  }

  private subscribeToPasteShapes() {
    this.subscribeTo(
      this.keyboardShortcutService.getEvent$('pasteShapes'),
      ({ keyboardEvent, eventTrackingValue }) =>
        this.pasteAnnotations(eventTrackingValue, keyboardEvent),
    );
  }

  private validateAndParseDataForPasting(clipboardText: string) {
    try {
      const copiedAnnotations: ClipboardAnnotationData =
        JSON.parse(clipboardText);
      this.pasteValidationService.validate(copiedAnnotations);
      return copiedAnnotations;
    } catch (e) {
      this.handlePasteError(e);
      return undefined;
    }
  }

  private handlePasteError(error: unknown) {
    console.error(error);
    let errorMsg = `The data you are trying to paste are invalid. Ensure that the copied data come from a compatible project and task.`;
    if (error instanceof PasteValidationError) {
      errorMsg += ` (Details: ${error.message})`;
    }
    this.matDialog.open(MessageModalComponent, {
      data: { title: 'Paste error', text: errorMsg },
    });
  }

  private trackPasteEvent(
    eventTrackingValue: EventTrackingValue,
    shapes: ShapeState[],
    objects: ObjectState[],
  ) {
    const shapesObject = mapToObject(shapes, (shape) => [shape.index, shape]);
    const indexes = shapes.map((shape) => shape.index);

    this.pushAndTrackService.pushAndTrack({
      id: 'paste_annotations',
      bucket: 'annotation',
      ...eventTrackingValue,
      shapes: getShapesForEventTracking(shapesObject, indexes),
      objects: objects.map((object) => ({
        id: object.index,
        parentId: object.parentId,
      })),
    });
  }
}
